import { BaseModal, FormActions, FormField, FormSectionTitle, FormSelect } from '@practium/ui';
import {
	BIENNIAL_LABELS,
	COFFEE_OPTIONS,
	RANGE_MODE_LABELS,
	REGION_LABELS,
	SEASON_LABELS,
} from '../../constants/parameters.constants';
import { useRangeCombinationForm } from '../../hooks/useRangeCombinationForm';
import type {
	CoffeeType,
	RangeCombination,
	RangeMode,
	RangesKind,
	ReferenceBiennial,
	ReferenceRegion,
	ReferenceSeason,
	StateReferenceDTO,
	StateReferenceRange,
} from '../../types/parameters.types';
import { RangeValuesFields } from './RangeValuesFields';
import styles from './Sections.module.css';

interface RangeCombinationModalProps {
	kind: RangesKind;
	reference: StateReferenceDTO;
	combinations: RangeCombination[];
	combination: RangeCombination | null;
	isSaving: boolean;
	onClose: () => void;
	onSubmit: (profiles: StateReferenceRange[]) => Promise<boolean>;
}

const toOptions = <T extends string>(labels: Record<T, string>) =>
	(Object.keys(labels) as T[]).map((value) => ({ value, label: labels[value] }));

const MODE_OPTIONS = toOptions(RANGE_MODE_LABELS);
const REGION_OPTIONS = toOptions(REGION_LABELS);
const BIENNIAL_OPTIONS = toOptions(BIENNIAL_LABELS);
const SEASON_OPTIONS = toOptions(SEASON_LABELS);

export const RangeCombinationModal = ({
	kind,
	reference,
	combinations,
	combination,
	isSaving,
	onClose,
	onSubmit,
}: RangeCombinationModalProps) => {
	const { input, baseId, baseOptions, error, setField, selectBase, setValue, submit } = useRangeCombinationForm({
		kind,
		reference,
		combinations,
		combination,
		onSubmit,
		onClose,
	});
	const noun = kind === 'foliar' ? 'níveis críticos foliares' : 'normas DRIS';

	return (
		<BaseModal
			isOpen
			wide
			title={combination ? `Editar ${noun}` : `Nova combinação de ${noun}`}
			subtitle={combination ? `${combination.title} · ${combination.context}` : undefined}
			isLoading={isSaving}
			onClose={onClose}
			actions={<FormActions onCancel={onClose} onSave={submit} isLoading={isSaving} />}
		>
			<div className={styles.form}>
				{!combination && (
					<div className={styles.formSection}>
						<FormSectionTitle>Combinação</FormSectionTitle>
						<div className={styles.fields}>
							<FormField label="Tipo de café" required>
								<FormSelect
									value={input.coffeeType}
									options={COFFEE_OPTIONS}
									onChange={(value) => setField('coffeeType', value as CoffeeType)}
								/>
							</FormField>
							<FormField label="Organização" required>
								<FormSelect
									value={input.mode}
									options={MODE_OPTIONS}
									onChange={(value) => setField('mode', value as RangeMode)}
								/>
							</FormField>
							{input.mode === 'region' && (
								<>
									<FormField label="Região do estado" required>
										<FormSelect
											value={input.region}
											options={REGION_OPTIONS}
											onChange={(value) => setField('region', value as ReferenceRegion)}
										/>
									</FormField>
									<FormField label="Bienalidade" required>
										<FormSelect
											value={input.biennial}
											options={BIENNIAL_OPTIONS}
											onChange={(value) => setField('biennial', value as ReferenceBiennial)}
										/>
									</FormField>
								</>
							)}
							{input.mode === 'season' && (
								<FormField label="Estação do ano" required>
									<FormSelect
										value={input.season}
										options={SEASON_OPTIONS}
										onChange={(value) => setField('season', value as ReferenceSeason)}
									/>
								</FormField>
							)}
							<div className={styles.fieldsFull}>
								<FormField label="Copiar valores de">
									<FormSelect value={baseId} options={baseOptions} onChange={selectBase} />
								</FormField>
							</div>
						</div>
					</div>
				)}
				<div className={styles.formSection}>
					<FormSectionTitle>Valores</FormSectionTitle>
					<RangeValuesFields kind={kind} rows={input.rows} onChange={setValue} />
				</div>
				{error && (
					<span className={styles.formError} role="alert">
						{error}
					</span>
				)}
			</div>
		</BaseModal>
	);
};
